import { ChevronDown, Plus, RotateCw, Search } from "lucide-react";
import { useDebounce } from "~/hooks/use-debounce";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAllSchemas } from "@noa/api-client";
import type { DocumentTypeSchema } from "@noa/api-core";
import { SchemaDeleteModal } from "~/components/admin/schema-delete-modal";
import { SchemaEditModal } from "~/components/admin/schema-edit-modal";
import { SchemaList } from "~/components/admin/schema-list";
import { SchemaViewModal } from "~/components/admin/schema-view-modal";
import { Screen } from "~/screens/screen";
import { SCHEMA_TEMPLATES, type SchemaTemplate } from "~/utils/schema-admin";
import { Alert, Button, Dropdown, Input, Title, type MenuProps } from "~/components/ui";

export function SchemaAdminScreen() {
  const { t } = useTranslation("admin");

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 300);

  const [selectedSchema, setSelectedSchema] = useState<DocumentTypeSchema | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<SchemaTemplate | null>(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [editMode, setEditMode] = useState<"create" | "edit">("create");

  const { schemas: allSchemas, isLoading, refetch, isError, error } = useAllSchemas();

  const filteredSchemas = useMemo(() => {
    if (!debouncedSearch) return allSchemas;
    const searchLower = debouncedSearch.toLowerCase();
    return allSchemas.filter(
      (schema) =>
        schema.schema_id.toLowerCase().includes(searchLower) ||
        schema.doc_type.toLowerCase().includes(searchLower) ||
        schema.description.toLowerCase().includes(searchLower)
    );
  }, [allSchemas, debouncedSearch]);

  const handleView = (schema: DocumentTypeSchema) => {
    setSelectedSchema(schema);
    setViewModalOpen(true);
  };

  const handleEdit = (schema: DocumentTypeSchema) => {
    setSelectedSchema(schema);
    setEditMode("edit");
    setEditModalOpen(true);
  };

  const handleDelete = (schema: DocumentTypeSchema) => {
    setSelectedSchema(schema);
    setDeleteModalOpen(true);
  };

  const handleCreate = () => {
    setSelectedSchema(null);
    setSelectedTemplate(null);
    setEditMode("create");
    setEditModalOpen(true);
  };

  const handleCreateFromTemplate = (template: SchemaTemplate) => {
    setSelectedSchema(null);
    setSelectedTemplate(template);
    setEditMode("create");
    setEditModalOpen(true);
  };

  const handleViewToEdit = () => {
    setViewModalOpen(false);
    setSelectedTemplate(null);
    setEditMode("edit");
    setEditModalOpen(true);
  };

  const templateMenuItems: MenuProps["items"] = SCHEMA_TEMPLATES.map((template) => ({
    key: template.id,
    label: (
      <div>
        <div className="font-medium">{t(`templates.${template.id}.name`)}</div>
        <div className="text-xs text-noa-muted">{t(`templates.${template.id}.description`)}</div>
      </div>
    ),
    onClick: () => handleCreateFromTemplate(template),
  }));

  if (isError) {
    return (
      <Screen>
        <div className="flex h-full w-full flex-col overflow-auto p-4 sm:p-6">
          <Alert
            message={t("schemaAdmin.error.title")}
            description={error?.message ?? t("schemaAdmin.error.unknown")}
            type="error"
            showIcon
            action={
              <Button size="small" onClick={() => refetch()}>
                {t("schemaAdmin.refresh")}
              </Button>
            }
          />
        </div>
      </Screen>
    );
  }

  return (
    <Screen>
      <div className="flex h-full w-full flex-col overflow-auto p-4 sm:p-6">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <Title level={3} className="m-0">
            {t("schemaAdmin.title")}
          </Title>

          <div className="flex gap-2">
            <Button icon={<RotateCw size={14} />} onClick={() => refetch()}>
              {t("schemaAdmin.refresh")}
            </Button>
            {/* Split button: create blank, or pick a template from the menu */}
            <div className="inline-flex">
              <Button variant="primary" icon={<Plus size={14} />} onClick={handleCreate} className="rounded-e-none">
                {t("schemaAdmin.create")}
              </Button>
              <Dropdown menu={{ items: templateMenuItems }} placement="bottomRight">
                <Button
                  variant="primary"
                  icon={<ChevronDown className="size-3.5" />}
                  className="rounded-s-none border-s border-s-white/30"
                  aria-label={t("schemaAdmin.create")}
                />
              </Dropdown>
            </div>
          </div>
        </div>

        <Input
          placeholder={t("schemaAdmin.search")}
          prefix={<Search size={14} />}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          allowClear
          className="mb-4 max-w-md"
        />

        <div className="flex-1 rounded-lg p-4 bg-noa">
          <SchemaList
            schemas={filteredSchemas}
            isLoading={isLoading}
            onView={handleView}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />
        </div>
      </div>

      <SchemaViewModal
        isOpen={viewModalOpen}
        setIsOpen={setViewModalOpen}
        schema={selectedSchema}
        onEdit={handleViewToEdit}
      />

      <SchemaEditModal
        isOpen={editModalOpen}
        setIsOpen={setEditModalOpen}
        schema={selectedSchema}
        mode={editMode}
        template={selectedTemplate}
      />

      <SchemaDeleteModal isOpen={deleteModalOpen} setIsOpen={setDeleteModalOpen} schema={selectedSchema} />
    </Screen>
  );
}
